/**
 * Talousarviosivun laskenta. Puhtaita funktioita, jotta ne voi testata ilman
 * sivua.
 *
 * Etumerkit: raportin riveillä (Tulos & Tase) tuotot ovat positiivisia ja kulut
 * negatiivisia. Talousarvioon kirjoitetaan aina positiivisia lukuja, ja myös
 * taulukossa kulut näytetään positiivisina, jotta talousarvio ja toteuma ovat
 * samannäköisiä. "Erotus" on toteuma miinus talousarvio raportin etumerkein,
 * joten se on aina positiivinen, kun toteuma on talousarviota parempi.
 */

/** Sama jako kuin backendin accounts/ranges.ts: 700, 800, 900 ja 910 ovat tuottoja */
const EXPENSE_ACCOUNT_RANGES: [number, number][] = [
  [400, 699],
  [850, 850],
];

export const isExpenseAccount = (code: number) =>
  EXPENSE_ACCOUNT_RANGES.some(([low, high]) => code >= low && code <= high);

export interface StatementAccount {
  code: number;
  name: string;
  value?: number;
}

export interface StatementRow {
  id: string;
  order: number;
  parentId?: string | null;
  name: string;
  value?: number;
  accounts?: StatementAccount[];
}

export interface BudgetDisplayLine {
  key: string;
  /** Ryhmärivin id; tilirivillä sen ryhmän id, johon tili kuuluu */
  groupId: string;
  depth: number;
  label: string;
  code?: number;
  isTotal: boolean;
  isAccount: boolean;
  budget: number;
  actual: number;
  previous: number | null;
  difference: number;
}

/** "1 200,50" → 1200.5; tyhjä → 0; kelvoton → NaN */
export const parseAmount = (input: string | number | undefined | null) => {
  if (input == null) return 0;
  if (typeof input === "number") return input;
  const normalized = input.replace(/[\s ]/g, "").replace(",", ".");
  if (normalized === "") return 0;
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return NaN;
  return Number(normalized);
};

const reportSign = (code: number) => (isExpenseAccount(code) ? -1 : 1);

/** Flipping the sign of 0 gives -0, which formats as "−0,00 €" */
const clean = (value: number) => (Math.abs(value) < 0.005 ? 0 : value);

const childrenOf = (rows: StatementRow[], parentId: string | null) =>
  rows
    .filter((row) => (row.parentId ?? null) === parentId)
    .sort((a, b) => a.order - b.order);

/**
 * Rakentaa taulukon rivit toteuman rivipuun mukaan. Talousarvio lasketaan
 * syötetyistä luvuista, jotta välisummat päivittyvät kirjoittaessa eikä vasta
 * tallennuksen jälkeen.
 */
export const buildBudgetLines = (
  actual: StatementRow[],
  previous: StatementRow[] | null,
  amounts: Record<number, string | number>
): BudgetDisplayLine[] => {
  const previousById = new Map((previous ?? []).map((row) => [row.id, row]));
  const hasPrevious = previous != null;

  interface Node {
    lines: BudgetDisplayLine[];
    /** Raportin etumerkein */
    budget: number;
    actual: number;
    previous: number;
    accountCodes: number[];
  }

  const walk = (row: StatementRow, depth: number): Node => {
    const prevRow = previousById.get(row.id);

    const accountNodes = (row.accounts ?? []).map((account) => {
      const sign = reportSign(account.code);
      const budget = sign * (parseAmount(amounts[account.code]) || 0);
      const actualValue = account.value ?? 0;
      const previousValue =
        prevRow?.accounts?.find(({ code }) => code === account.code)?.value ??
        0;
      return {
        line: {
          key: `account-${account.code}`,
          groupId: row.id,
          depth: depth + 1,
          label: `${account.code} ${account.name}`,
          code: account.code,
          isTotal: false,
          isAccount: true,
          budget: clean(sign * budget),
          actual: clean(sign * actualValue),
          previous: hasPrevious ? clean(sign * previousValue) : null,
          difference: clean(actualValue - budget),
        },
        budget,
        actual: actualValue,
        previous: previousValue,
        code: account.code,
      };
    });

    const childNodes = childrenOf(actual, row.id).map((child) =>
      walk(child, depth + 1)
    );

    const budget =
      accountNodes.reduce((sum, node) => sum + node.budget, 0) +
      childNodes.reduce((sum, node) => sum + node.budget, 0);
    const actualValue = row.value ?? 0;
    const previousValue = prevRow?.value ?? 0;
    const accountCodes = [
      ...accountNodes.map(({ code }) => code),
      ...childNodes.flatMap((node) => node.accountCodes),
    ];

    // Pelkkiä kuluja sisältävä ryhmä näytetään positiivisena kuten sen tilit;
    // tuottoja ja kuluja sekoittava ryhmä näyttää nettoluvun.
    const displaySign =
      accountCodes.length > 0 && accountCodes.every(isExpenseAccount) ? -1 : 1;

    const own: BudgetDisplayLine = {
      key: `row-${row.id}`,
      groupId: row.id,
      depth,
      label: depth === 0 ? "Ylijäämä / alijäämä" : row.name,
      isTotal: depth === 0,
      isAccount: false,
      budget: clean(displaySign * budget),
      actual: clean(displaySign * actualValue),
      previous: hasPrevious ? clean(displaySign * previousValue) : null,
      difference: clean(actualValue - budget),
    };

    const below = [
      ...accountNodes.map(({ line }) => line),
      ...childNodes.flatMap((node) => node.lines),
    ];
    return {
      // Ryhmän otsikko välisummineen ennen sisältöään; kokonaistulos viimeisenä
      lines: depth === 0 ? [...below, own] : [own, ...below],
      budget,
      actual: actualValue,
      previous: previousValue,
      accountCodes,
    };
  };

  return childrenOf(actual, null).flatMap((root) => walk(root, 0).lines);
};

/**
 * "Täytä edellisen vuoden toteumalla": edellisen vuoden luku positiivisena.
 * Väärinpäin kirjattu (esim. kulutilin kredit-saldo) jätetään tyhjäksi, koska
 * negatiivista talousarviota ei ole.
 */
export const amountsFromPreviousActual = (
  previous: StatementRow[] | null
): Record<number, string> => {
  const result: Record<number, string> = {};
  for (const row of previous ?? []) {
    for (const account of row.accounts ?? []) {
      const value = reportSign(account.code) * (account.value ?? 0);
      if (value > 0.004) {
        result[account.code] = formatInputAmount(value);
      }
    }
  }
  return result;
};

/** Syöttökenttään: 1200.5 → "1200,50" */
export const formatInputAmount = (value: number) =>
  value.toFixed(2).replace(".", ",");

/** Tallennettavat rivit; kelvoton syöte palauttaa sen tilin koodin virheenä */
export const toBudgetLines = (
  amounts: Record<number, string | number>
): { lines: { accountCode: number; amount: number }[]; invalid: number[] } => {
  const lines: { accountCode: number; amount: number }[] = [];
  const invalid: number[] = [];
  for (const [code, input] of Object.entries(amounts)) {
    const amount = parseAmount(input);
    if (Number.isNaN(amount)) {
      invalid.push(Number(code));
    } else if (amount > 0) {
      lines.push({ accountCode: Number(code), amount });
    }
  }
  return { lines, invalid };
};
